import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Spinner } from "@/components/UIKit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDateTime, roleLabel } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, User as UserIcon, Building2, KeyRound, Loader2, Plus, Plug } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Integrasjoner from "./Integrasjoner";
import Invitasjoner from "./Invitasjoner";

export default function Innstillinger() {
  const { user, tenant } = useAuth();

  return (
    <div data-testid="innstillinger-page">
      <PageHeader title="Innstillinger" subtitle="Konfigurasjon, brukere og sikkerhet" />

      <Tabs defaultValue="tenant">
        <TabsList className="bg-zinc-950 border border-zinc-900 h-10">
          <TabsTrigger value="tenant" data-testid="tab-tenant"><Building2 className="h-3.5 w-3.5 mr-1.5" /> Tenant</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><UserIcon className="h-3.5 w-3.5 mr-1.5" /> Brukere</TabsTrigger>
          <TabsTrigger value="invitations" data-testid="tab-invitations">Portal-invitasjoner</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations"><Plug className="h-3.5 w-3.5 mr-1.5" /> Integrasjoner</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Sikkerhet</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit-logg</TabsTrigger>
        </TabsList>

        <TabsContent value="tenant" className="mt-4"><TenantSettings tenant={tenant} /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersSettings /></TabsContent>
        <TabsContent value="invitations" className="mt-4"><Invitasjoner /></TabsContent>
        <TabsContent value="integrations" className="mt-4"><Integrasjoner /></TabsContent>
        <TabsContent value="security" className="mt-4"><SecuritySettings user={user} /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditLog /></TabsContent>
      </Tabs>
    </div>
  );
}

function TenantSettings({ tenant }) {
  const [form, setForm] = useState(tenant || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(tenant || {}); }, [tenant]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/tenant", form);
      toast.success("Innstillinger lagret");
    } catch { toast.error("Kunne ikke lagre"); }
    finally { setSaving(false); }
  };

  const field = (name, label, extra = {}) => (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</label>
      <input value={form[name] ?? ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm focus:outline-none focus:border-emerald-500/40"
        {...extra} data-testid={`tenant-${name}`} />
    </div>
  );

  return (
    <Card className="p-6 max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {field("name", "Selskapsnavn")}
        {field("orgnr", "Organisasjonsnummer", { disabled: true })}
        {field("contact_email", "Kontakt e-post", { type: "email" })}
        {field("contact_phone", "Telefon")}
        {field("address", "Adresse")}
        <div className="grid grid-cols-2 gap-3">
          {field("postal_code", "Postnr")}
          {field("city", "Sted")}
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Standard prisområde</label>
          <select value={form.default_price_area || "NO1"} onChange={(e) => setForm({ ...form, default_price_area: e.target.value })}
            className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm">
            {["NO1", "NO2", "NO3", "NO4", "NO5"].map((a) => <option key={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Elavgift (øre/kWh)</label>
          <input type="number" step="0.01" value={form.electricity_tax_ore_per_kwh || 0}
            onChange={(e) => setForm({ ...form, electricity_tax_ore_per_kwh: Number(e.target.value) })}
            className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm tabular-nums" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">MVA-sats</label>
          <input type="number" step="0.01" value={form.vat_rate || 0}
            onChange={(e) => setForm({ ...form, vat_rate: Number(e.target.value) })}
            className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm tabular-nums" />
        </div>
      </div>
      <div className="mt-5">
        <button onClick={save} disabled={saving}
          className="h-10 px-5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium disabled:opacity-60 flex items-center gap-2" data-testid="save-tenant">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Lagre
        </button>
      </div>
    </Card>
  );
}

function UsersSettings() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => setItems((await api.get("/settings/users")).data);
  useEffect(() => { load(); }, []);

  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b border-zinc-900 flex items-center justify-between">
        <div className="font-heading font-medium">Ansatte</div>
        <button onClick={() => setOpen(true)}
          className="h-8 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-medium flex items-center gap-1.5"
          data-testid="add-user">
          <Plus className="h-3.5 w-3.5" /> Ny bruker
        </button>
      </div>
      {items === null ? <div className="p-6"><Spinner /></div> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
              <th className="py-2 px-4 font-medium">Navn</th>
              <th className="py-2 px-4 font-medium">E-post</th>
              <th className="py-2 px-4 font-medium">Rolle</th>
              <th className="py-2 px-4 font-medium">2FA</th>
              <th className="py-2 px-4 font-medium">Siste innlogging</th>
              <th className="py-2 px-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-zinc-900">
                <td className="py-2 px-4 text-zinc-100">{u.full_name}</td>
                <td className="py-2 px-4 text-zinc-400 text-xs">{u.email}</td>
                <td className="py-2 px-4"><span className="text-xs bg-zinc-800 px-1.5 py-0.5 rounded">{roleLabel(u.role)}</span></td>
                <td className="py-2 px-4 text-xs">{u.totp_enabled ? <span className="text-emerald-400">Aktivert</span> : <span className="text-zinc-500">—</span>}</td>
                <td className="py-2 px-4 text-zinc-500 text-xs">{fmtDateTime(u.last_login_at)}</td>
                <td className="py-2 px-4 text-xs">{u.is_active ? <span className="text-emerald-400">Aktiv</span> : <span className="text-red-400">Sperret</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <NyBrukerDialog open={open} onOpenChange={setOpen} onCreated={() => { setOpen(false); load(); }} />
    </Card>
  );
}

function NyBrukerDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "kundeservice", linked_customer_id: "" });
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    if (open) api.get("/customers?kind=bedrift&limit=200").then(({ data }) => setCustomers(data.items || []));
  }, [open]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      if (payload.role !== "bedriftsbruker") delete payload.linked_customer_id;
      if (!payload.linked_customer_id) delete payload.linked_customer_id;
      await api.post("/settings/users", payload);
      toast.success("Bruker opprettet"); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900">
        <DialogHeader>
          <DialogTitle className="font-heading">Ny bruker</DialogTitle>
          <DialogDescription className="sr-only">Opprett en ny ansattbruker</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Fullt navn" required className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-post" required className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Passord (minst 10 tegn)" minLength={10} required className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm">
            {["admin", "kundeservice", "okonomi", "drift", "viewer", "bedriftsbruker"].map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
          {form.role === "bedriftsbruker" && (
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1">Tilknytt bedriftskunde</label>
              <select value={form.linked_customer_id} onChange={(e) => setForm({ ...form, linked_customer_id: e.target.value })} required
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid="link-customer">
                <option value="">— velg bedrift —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_number} · {c.full_name}</option>)}
              </select>
            </div>
          )}
          <button type="submit" disabled={busy} className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Opprett
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SecuritySettings({ user }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [enabled, setEnabled] = useState(user?.totp_enabled);

  const start = async () => {
    const { data } = await api.post("/auth/2fa/setup");
    setSetup(data);
  };
  const verify = async () => {
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA aktivert"); setEnabled(true); setSetup(null);
    } catch { toast.error("Ugyldig kode"); }
  };
  const disable = async () => {
    await api.post("/auth/2fa/disable"); setEnabled(false); toast.success("2FA deaktivert");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-emerald-400" />
          <div className="font-heading font-medium">Tofaktor-autentisering (TOTP)</div>
        </div>
        <div className="text-sm text-zinc-400 mb-4">
          Beskytt kontoen din med engangskoder fra Google Authenticator, Authy, 1Password eller lignende.
        </div>
        {enabled ? (
          <div>
            <div className="text-sm text-emerald-400 mb-3 flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Aktivert på kontoen</div>
            <button onClick={disable} className="h-9 px-4 rounded-md border border-red-500/30 text-red-400 text-sm hover:bg-red-500/10">Deaktiver</button>
          </div>
        ) : setup ? (
          <div>
            <div className="text-xs text-zinc-500 mb-2">Skann QR-kode eller skriv inn hemmeligheten manuelt:</div>
            <div className="font-mono text-xs bg-zinc-900 p-3 rounded border border-zinc-800 mb-3 break-all select-all">{setup.secret}</div>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              maxLength={6} placeholder="6-sifret kode"
              className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-center tracking-widest text-sm mb-2" />
            <button onClick={verify} className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium">Bekreft og aktiver</button>
          </div>
        ) : (
          <button onClick={start} className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Sett opp 2FA
          </button>
        )}
      </Card>

      <Card className="p-5">
        <div className="font-heading font-medium mb-3">Endre passord</div>
        <PasswordChange />
      </Card>
    </div>
  );
}

function PasswordChange() {
  const [cur, setCur] = useState(""); const [ne, setNe] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: ne });
      toast.success("Passord endret"); setCur(""); setNe("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Gjeldende passord" required className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
      <input type="password" value={ne} onChange={(e) => setNe(e.target.value)} placeholder="Nytt passord (minst 10 tegn)" minLength={10} required className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
      <button type="submit" disabled={busy} className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium disabled:opacity-60 flex items-center gap-2">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Oppdater passord
      </button>
    </form>
  );
}

function AuditLog() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/settings/audit?limit=100").then(({ data }) => setItems(data.items)); }, []);
  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b border-zinc-900 font-heading font-medium">Siste 100 hendelser</div>
      {!items ? <div className="p-6"><Spinner /></div> :
      items.length === 0 ? <div className="p-6 text-sm text-zinc-500">Ingen hendelser ennå</div> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
              <th className="py-2 px-4 font-medium">Tid</th>
              <th className="py-2 px-4 font-medium">Bruker</th>
              <th className="py-2 px-4 font-medium">Handling</th>
              <th className="py-2 px-4 font-medium">Ressurs</th>
              <th className="py-2 px-4 font-medium">IP</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a, i) => (
              <tr key={i} className="border-t border-zinc-900">
                <td className="py-1.5 px-4 text-zinc-500 text-xs">{fmtDateTime(a.ts)}</td>
                <td className="py-1.5 px-4 text-zinc-300 text-xs">{a.user_email || "system"}</td>
                <td className="py-1.5 px-4"><span className="text-xs font-mono text-emerald-400">{a.action}</span></td>
                <td className="py-1.5 px-4 text-zinc-400 text-xs">{a.resource_type || "—"} {a.resource_id ? a.resource_id.slice(0, 8) : ""}</td>
                <td className="py-1.5 px-4 text-zinc-500 text-xs">{a.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
